import React, { useState, useEffect, useRef } from "react";
import {
  ShoppingCart,
  Search,
  Plus,
  Trash2,
  Package,
  ChevronDown,
  ChevronUp,
  Send,
  AlertCircle,
  X,
  CheckCircle2,
} from "lucide-react";
import { useGlobalContext } from "../../context/stateContext";
import { useAuth } from "../../context/Auth";
import { toast } from "react-toastify";
import { RotatingLines } from "react-loader-spinner";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const NewWarehouseStockRequisition = () => {
  const navigate = useNavigate();
  const searchRef = useRef();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [drugsDropdown, setDrugsDropdown] = useState(false);
  const [quantityRequested, setQuantityRequested] = useState(null);
  const [drugs, setDrugs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [insertedDrugs, setInsertedDrugs] = useState([]);
  const [totalCost, setTotalCost] = useState(0.0);
  const [requestingDrugs, setRequestingDrugs] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(-1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const quantityInputRef = useRef();
  const listContainerRef = useRef(null);

  const requestDrugs = async () => {
    if (insertedDrugs.length <= 0) {
      return toast.error("Insert at least one drug");
    }
    Swal.fire({
      icon: "question",
      title: "Are you sure you want to request for these items?",
      showDenyButton: true,
      confirmButtonText: "Yes Proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setRequestingDrugs(true);
          const body = JSON.stringify({
            is_warehouse_requisition: true,
            branch: activeSession?.branch?.id,
            drugs: insertedDrugs,
          });

          const data = await requestWithAuth(
            `/inventory/requisition/branch/`,
            "POST",
            body,
            {
              "Content-Type": "application/json",
            }
          );
          setSuccessData(data);
          setShowSuccessModal(true);
        } catch (error) {
          toast.error(error.response.data.message);
        } finally {
          setRequestingDrugs(false);
        }
      } else if (result.isDenied) {
        Swal.fire("Changes are not saved", "", "info");
      }
    });
  };

  const selectDrug = (drug) => {
    setSelectedDrug(drug);
    setDrugsDropdown(false);
    setSearch("");
    setDrugs([]);
    setSelectedResultIndex(-1);
  };

  const getDrugs = async (search_query) => {
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/inventory/branch/${activeSession?.branch?.id}?paginated=false&search=${search_query}`,
        "GET"
      );
      setDrugs(data);
    } catch (error) {
      console.error("Error fetching users data:", error);
    } finally {
      setLoading(false);
      setSelectedResultIndex(-1);
    }
  };

  const insertDrug = () => {
    if (selectedDrug) {
      if (quantityRequested) {
        const drugId = selectedDrug?.drug?.id;

        // check if the drug is already in the list
        const isAlreadyInserted = insertedDrugs.some(
          (item) => item.drug === drugId
        );

        if (isAlreadyInserted) {
          toast.error("This drug is already in the list");
          return;
        }
        const total_selling_price =
          parseFloat(selectedDrug?.drug?.selling_price) *
          parseFloat(quantityRequested);
        const newInsertedDrugs = [
          ...insertedDrugs,
          {
            drug: selectedDrug?.drug?.id,
            drug_name: selectedDrug?.drug?.name,
            quantity: quantityRequested,
            unit_cost: selectedDrug?.drug?.selling_price,
            total_selling_price: total_selling_price.toFixed(2),
            is_warehouse_requisition: true,
          },
        ];
        setSelectedDrug(null);
        setInsertedDrugs(newInsertedDrugs);
        setQuantityRequested("");

        let total = 0;
        newInsertedDrugs.map((item) => {
          total += parseFloat(item?.total_selling_price);
        });
        setTotalCost(total.toFixed(2));
        setDrugsDropdown(true);
      } else {
        toast.error("Please enter the quantity");
      }
    } else {
      toast.error("Select a drug first");
    }
  };

  const removeInsertedDrug = (index) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs.splice(index, 1);
    setInsertedDrugs(newInsertedDrugs);
    let total = 0;
    newInsertedDrugs.map((item) => {
      total += parseFloat(item?.total_selling_price);
    });
    setTotalCost(total.toFixed(2));
  };

  const updateInsertedDrug = (index, new_quantity) => {
    const newInsertedDrugs = [...insertedDrugs];
    newInsertedDrugs[index].quantity = new_quantity;
    newInsertedDrugs[index].total_selling_price =
      parseFloat(newInsertedDrugs[index].unit_cost) *
      parseFloat(newInsertedDrugs[index].quantity);
    setInsertedDrugs(newInsertedDrugs);
    let total = 0;
    newInsertedDrugs.map((item) => {
      total += parseFloat(item?.total_selling_price);
    });
    setTotalCost(total.toFixed(2));
  };

  useEffect(() => {
    if (search.length > 2) {
      getDrugs(search);
    }
  }, [search]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.altKey && event.key.toLowerCase() === "s") {
        event.preventDefault();
        requestDrugs();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [insertedDrugs]);

  useEffect(() => {
    if (drugsDropdown) {
      setTimeout(() => {
        searchRef.current?.focus();
      }, 0);
    } else {
      setSelectedResultIndex(-1);
    }
  }, [drugsDropdown]);

  const handleSearchKeyDown = (e) => {
    if (!drugs.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedResultIndex((prev) =>
        prev < drugs.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedResultIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (selectedResultIndex >= 0 && selectedResultIndex < drugs.length) {
        selectDrug(drugs[selectedResultIndex]);
      } else if (!drugsDropdown) {
        insertDrug();
      }
    }
  };

  useEffect(() => {
    if (selectedResultIndex >= 0 && listContainerRef.current) {
      const selectedElement = listContainerRef.current.querySelector(
        `#drug-result-${selectedResultIndex}`
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedResultIndex]);

  useEffect(() => {
    if (selectedDrug && quantityInputRef.current) {
      setTimeout(() => {
        quantityInputRef.current?.focus();
      }, 100);
    }
  }, [selectedDrug]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  New Requisition
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Package size={32} />
                Warehouse Stock Requisition
              </h1>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <p className="text-sm text-gray-600">Source Branch</p>
              <p className="text-lg font-semibold text-gray-900">
                {activeSession?.branch?.name}
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Panel - Requisition Entry */}
          <div className="lg:col-span-2 space-y-6">
            {/* Product Selection Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <Package className="text-blue-500" size={24} />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                      Requisition Details
                    </h2>
                    <p className="text-sm text-gray-500">
                      Search and add items to requisition
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                {/* Drug Search */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Drug
                  </label>
                  <div
                    className="relative cursor-pointer border border-gray-300 rounded-lg px-4 py-2.5 text-sm flex justify-between items-center"
                    onClick={() => {
                      setDrugsDropdown(!drugsDropdown);
                      searchRef.current?.focus();
                    }}
                  >
                    <span
                      className={
                        selectedDrug?.drug?.name
                          ? "text-gray-900"
                          : "text-gray-400"
                      }
                    >
                      {selectedDrug?.drug?.name || "Select a drug..."}
                    </span>
                    {drugsDropdown ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>

                  {drugsDropdown && (
                    <div
                      onClick={(e) => e.stopPropagation()}
                      className="absolute z-10 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-lg"
                    >
                      <div className="p-3 border-b border-gray-200">
                        <div className="relative">
                          <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            size={18}
                          />
                          <input
                            ref={searchRef}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            type="text"
                            placeholder="Type to search..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="max-h-64 overflow-y-auto" ref={listContainerRef}>
                        {search?.length < 3 ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Please enter at least 3 characters
                          </p>
                        ) : loading ? (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            Searching...
                          </p>
                        ) : drugs?.length > 0 ? (
                          drugs.map((drug_item, idx) => (
                            <div
                              key={idx}
                              id={`drug-result-${idx}`}
                              onClick={() => selectDrug(drug_item)}
                              className={`p-3 hover:bg-blue-300 cursor-pointer border-b border-gray-100 last:border-0 ${selectedResultIndex === idx ? "bg-blue-300" : ""
                                }`}
                            >
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-900">
                                  {drug_item?.drug?.name}
                                </span>
                                <span className="text-sm font-semibold text-blue-600">
                                  GHS {drug_item?.drug?.selling_price}
                                </span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="p-4 text-sm text-gray-500 text-center">
                            No results found
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Product Details Grid */}
                {selectedDrug?.drug && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Drug Name
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          {selectedDrug?.drug?.name}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Unit Price
                        </label>
                        <div className="text-sm font-semibold text-gray-900">
                          GHS {selectedDrug?.drug?.selling_price}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Quantity
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={quantityRequested}
                          onChange={(e) => setQuantityRequested(e.target.value)}
                          placeholder="0"
                          ref={quantityInputRef}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              insertDrug();
                            }
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Add Button */}
                <button
                  onClick={insertDrug}
                  disabled={!selectedDrug?.drug || !quantityRequested}
                  className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <Plus size={20} />
                  Add to Requisition
                </button>
              </div>
            </div>
          </div>

          {/* Right Panel - Cart */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 sticky top-6">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ShoppingCart size={20} />
                  Requisition Cart ({insertedDrugs.length})
                </h2>
              </div>

              <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                {insertedDrugs.length > 0 ? (
                  <div className="divide-y divide-gray-200">
                    {insertedDrugs.map((drug, index) => (
                      <div key={index} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              {drug?.drug_name}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              ID: {drug?.drug}
                            </p>
                          </div>
                          <button
                            onClick={() => removeInsertedDrug(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>

                        <div className="flex items-center justify-between gap-2 mt-3">
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              value={drug?.quantity}
                              onChange={(e) =>
                                updateInsertedDrug(index, e.target.value)
                              }
                              className="w-16 border border-gray-300 rounded px-2 py-1 text-sm text-center"
                            />
                            <span className="text-xs text-gray-500">
                              × GHS {drug?.unit_cost}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">
                            GHS {drug?.total_selling_price}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <ShoppingCart
                      size={48}
                      className="mx-auto text-gray-300 mb-3"
                    />
                    <p className="text-sm text-gray-500">Your cart is empty</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Add items to get started
                    </p>
                  </div>
                )}
              </div>

              {insertedDrugs.length > 0 && (
                <div className="border-t border-gray-200">
                  <div className="p-6 bg-gray-50">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-gray-900">
                        Total Cost
                      </span>
                      <span className="text-2xl font-bold text-blue-600">
                        GHS {totalCost}
                      </span>
                    </div>

                    {/* Request Button */}
                    <button
                      onClick={requestDrugs}
                      disabled={requestingDrugs}
                      className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-sm flex items-center justify-center gap-2"
                    >
                      {requestingDrugs ? (
                        <>
                          <RotatingLines
                            visible={true}
                            height="20"
                            width="20"
                            color="white"
                            strokeColor="white"
                            strokeWidth="5"
                            animationDuration="0.75"
                            ariaLabel="rotating-lines-loading"
                          />
                          Requesting Items...
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          Request Drugs (Alt+S)
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden transform animate-in zoom-in-95 duration-300">
            {/* Header with Success Icon */}
            <div className="bg-green-50 p-8 text-center border-b border-green-100">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4 ring-8 ring-green-50">
                <CheckCircle2 size={48} className="text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Success!</h2>
              <p className="text-green-700 font-medium mt-1">
                {successData?.message || "Requisition processed successfully"}
              </p>
            </div>

            {/* Content Section */}
            <div className="p-6">
              {successData?.skipped_drugs?.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 py-2 px-3 bg-amber-50 rounded-lg border border-amber-100">
                    <AlertCircle size={18} className="text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800">
                      Some drugs were skipped
                    </span>
                  </div>

                  <div className="max-h-48 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                    {successData.skipped_drugs.map((item, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <p className="text-sm font-bold text-gray-800">
                          {item.drug}
                        </p>
                        <p className="text-xs text-gray-600 mt-0.5 mt-1 flex items-start gap-1.5">
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mt-1 flex-shrink-0" />
                          {item.reason}
                        </p>
                      </div>
                    ))}
                  </div>

                  <p className="text-xs text-gray-500 text-center italic">
                    Other items in your requisition were successfully created.
                  </p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-600">
                    All items in your requisition have been successfully
                    submitted for processing.
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex flex-col gap-3">
              <button
                onClick={() => {
                  navigate("/warehouse-manager/inventory/requisition");
                }}
                className="w-full bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-md active:scale-[0.98] flex items-center justify-center gap-2"
              >
                Proceed to Requisitions List
                <Send size={18} />
              </button>

              <button
                onClick={() => {
                  setShowSuccessModal(false);
                  setInsertedDrugs([]);
                  setTotalCost(0.0);
                  setSelectedDrug(null);
                  setQuantityRequested("");
                }}
                className="w-full bg-white hover:bg-gray-50 text-gray-600 px-6 py-2.5 rounded-xl font-medium transition-colors border border-gray-200"
              >
                Create New Requisition
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewWarehouseStockRequisition;
